import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { toEpochMs } from '@/lib/clock';

/**
 * List-view state that lives in the URL.
 *
 * An operator's place in a queue — the tab, the filter, the sort, the page they scrolled to — is
 * part of *where they are*, not a private detail of a mounted component. Kept in `useState` it
 * dies the moment they open a row, so the back button returns them to the top of the default tab
 * and they re-navigate by hand every time. Kept here, in the query string, the same view survives
 * the back button, a reload, a bookmark, and a link pasted to a colleague — and a detail screen
 * can carry it as a return address (see `return-to.ts`).
 *
 * Three properties are worth stating, because each is a decision rather than a consequence:
 *
 * - **Defaults never appear in the URL.** A screen opened from the sidebar reads
 *   `/ops/withdrawals`, not `/ops/withdrawals?tab=WAITING&status=ALL&page=0&…`. Only a departure
 *   from the default is worth a param, which is also what makes the URL legible at a glance.
 * - **Writes replace rather than push.** Typing six characters into a search box must not put six
 *   entries in the browser's history; the back button should leave the screen, not walk backwards
 *   through the operator's own filtering.
 * - **Reads are total.** Every param has a reader that returns `undefined` for anything it does
 *   not recognise, and the default stands in. A hand-edited, truncated or hostile query string
 *   therefore degrades to a sensible view instead of throwing inside a render.
 */

/** What one key of a view may hold. A query string carries strings and numbers, nothing else. */
export type ListStateValue = string | number | undefined;

/**
 * Constrains a view to values that survive a round trip through a URL.
 *
 * Written as `TState extends UrlSafe<TState>` rather than as an index signature, because an
 * interface — which is how these views are declared — has no implicit index signature and would
 * not satisfy `Record<string, …>` however well its fields fit.
 */
export type UrlSafe<TState> = { readonly [K in keyof TState]: ListStateValue };

/** Turns one raw param into a value the screen accepts, or `undefined` to keep the default. */
export type ParamReader<TValue> = (raw: string | null) => TValue | undefined;

/** A list screen's URL contract: where it lives, what it opens on, and what it will accept. */
export interface ListStateDef<TState extends UrlSafe<TState>> {
  /** The screen's own path — the href its state is written back to. */
  readonly path: string;
  /** The view with no params at all. Values equal to these are kept out of the URL. */
  readonly defaults: TState;
  /** One reader per key. Anything a reader rejects falls back to {@link defaults}. */
  readonly readers: {
    readonly [K in keyof TState]-?: ParamReader<Exclude<TState[K], undefined>>;
  };
}

/** What {@link useListState} hands a screen. */
export interface ListStateHandle<TState extends UrlSafe<TState>> {
  /** The current view, every key present. */
  readonly state: TState;
  /** Change part of the view. Unmentioned keys keep their current value. */
  readonly set: (patch: Partial<TState>) => void;
  /** This exact view as a path — what a detail screen needs in order to come back to it. */
  readonly href: string;
}

/**
 * The readers a list screen builds its params from.
 *
 * Each is deliberately narrow: a param is either recognisably one of the values the screen
 * offers, or it is not there at all. Nothing here trusts a length or a character class it has
 * not checked, because these strings arrive from whatever is in the address bar.
 */
export const readParam = {
  /** One of a fixed vocabulary — a tab, a status, a sort key. */
  oneOf<TValue extends string>(allowed: readonly TValue[]): ParamReader<TValue> {
    return (raw) =>
      raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as TValue) : undefined;
  },

  /** Free text, capped: a search box's contents, not a place to park a payload. */
  text(maxLength: number): ParamReader<string> {
    return (raw) => (raw === null || raw.length > maxLength ? undefined : raw);
  },

  /** A decimal the screen will echo into a numeric filter. */
  decimal(maxLength: number): ParamReader<string> {
    return (raw) =>
      raw !== null && raw.length <= maxLength && /^\d*(?:\.\d*)?$/.test(raw) ? raw : undefined;
  },

  /** A zero-based page index, bounded so a huge offset cannot be linked into the server. */
  index(max: number): ParamReader<number> {
    return (raw) => {
      if (raw === null || !/^\d{1,7}$/.test(raw)) return undefined;
      const value = Number(raw);
      return value <= max ? value : undefined;
    };
  },

  /**
   * An ISO-8601 instant *with an offset* — one end of an absolute range.
   *
   * Stricter than "something `Date` can parse", and deliberately so. The ops endpoints that take a
   * range declare `z.string().datetime({ offset: true })`, which refuses a date-only value and a
   * zoneless local one; a reader that accepted `2026-08-05` would turn a truncated URL into a
   * request the API answers with a 400 rather than into the default view this module promises. The
   * grammar is checked first and the value parsed second, because the shape alone cannot rule out
   * a month of `13`.
   *
   * Note what this deliberately does *not* judge: whether the instant is in the past, or how far it
   * sits from its partner. Those are properties of the *pair*, not of either string, and they live
   * with the picker that enforces the same bounds when an operator types them — see
   * `components/layout/range-picker.tsx`'s `readCustomRange`.
   */
  instant(): ParamReader<string> {
    return (raw) => {
      if (raw === null) return undefined;
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(raw)) {
        return undefined;
      }
      return Number.isNaN(toEpochMs(raw)) ? undefined : raw;
    };
  },

  /** One of the numeric sizes the screen's pager actually offers. */
  size(allowed: readonly number[]): ParamReader<number> {
    return (raw) => {
      if (raw === null || !/^\d{1,7}$/.test(raw)) return undefined;
      const value = Number(raw);
      return allowed.includes(value) ? value : undefined;
    };
  },
} as const;

/**
 * The same definition with its key names forgotten.
 *
 * The two functions below walk a definition key by key, which a mapped type cannot express
 * without knowing the keys. Erasing once here — rather than casting at each access — keeps the
 * looseness to a single line whose safety argument is easy to check: both members come from the
 * same object, and every key of `defaults` has a reader by construction of {@link ListStateDef}.
 */
interface ErasedDef {
  readonly defaults: Readonly<Record<string, ListStateValue>>;
  readonly readers: Readonly<Record<string, ParamReader<string | number> | undefined>>;
}

/** Read a screen's view out of a raw query string. Never throws; unknown params are ignored. */
export function readListState<TState extends UrlSafe<TState>>(
  def: ListStateDef<TState>,
  searchStr: string,
): TState {
  const { defaults, readers } = def as unknown as ErasedDef;
  const params = new URLSearchParams(searchStr);
  const state: Record<string, ListStateValue> = { ...defaults };
  for (const key of Object.keys(defaults)) {
    const read = readers[key];
    if (read === undefined) continue;
    const value = read(params.get(key));
    if (value !== undefined) state[key] = value;
  }
  // Safe by construction: `state` starts as a copy of `defaults` and every write above came from
  // that key's own reader, so each key still holds a value of its declared type.
  return state as TState;
}

/**
 * Build the path for a view — the inverse of {@link readListState}.
 *
 * Params are emitted in `defaults` key order and only when they differ from the default, so the
 * same view always produces the same string. That stability is what lets a return address be
 * compared, cached and canonicalised instead of merely round-tripped.
 */
export function listHref<TState extends UrlSafe<TState>>(
  def: ListStateDef<TState>,
  state: TState,
): string {
  const { defaults } = def as unknown as ErasedDef;
  const current = state as Readonly<Record<string, ListStateValue>>;
  const params = new URLSearchParams();
  for (const key of Object.keys(defaults)) {
    const value = current[key];
    if (value === undefined || value === defaults[key]) continue;
    params.set(key, String(value));
  }
  const query = params.toString();
  return query === '' ? def.path : `${def.path}?${query}`;
}

/**
 * The deploy sub-path (Vite's `base`) in front of a route path.
 *
 * The router adds it when it navigates; an `href` attribute rendered by hand has to add it
 * itself, or a middle-click on a back link would leave the app.
 */
export function withBasePath(href: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  return base === '' ? href : `${base}${href}`;
}

/**
 * Whether the browser would treat this click as "navigate here, in this tab".
 *
 * The other half of {@link withBasePath}: an in-app link built from a computed href is a real
 * `<a href>` — so that middle-click, ⌘-click and "open in new tab" work and a screen reader
 * announces a link — and the click handler that keeps a *plain* click inside the SPA must then be
 * careful to take over only that one click. Every modified click, and any anchor with a `target`,
 * belongs to the browser.
 *
 * Lives here beside `withBasePath` because the two are never used apart, and because a second
 * hand-rolled copy of this predicate would be a middle-click that silently stopped opening a new
 * tab on exactly one screen.
 */
export function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    event.currentTarget.target === ''
  );
}

/** Keep a list screen's view in the URL. See the module docs for the three rules this follows. */
export function useListState<TState extends UrlSafe<TState>>(
  def: ListStateDef<TState>,
): ListStateHandle<TState> {
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const navigate = useNavigate();
  const state = useMemo(() => readListState(def, searchStr), [def, searchStr]);
  const href = useMemo(() => listHref(def, state), [def, state]);

  const set = useCallback(
    (patch: Partial<TState>) => {
      // `replace`: filtering is one destination the operator is adjusting, not a trail of them.
      void navigate({ href: listHref(def, { ...state, ...patch }), replace: true });
    },
    [def, navigate, state],
  );

  return { state, set, href };
}

/** How long a search box holds a keystroke before it becomes part of the view. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * A free-text filter the box shows at once and the view learns about a beat later.
 *
 * Bound straight to {@link ListStateHandle.set}, a search box writes on every keystroke — and
 * since the view is part of the query key, an eight-character search is eight fetches, each one
 * a full search of the pool server-side. The keystrokes still have to *render* immediately: an
 * input that waits on a round trip feels broken and loses characters to a mid-typing re-render.
 * So the typed text lives here, in the component, and only the write into the URL is delayed.
 *
 * The URL stays the source of truth. A value arriving in `value` that this hook did not itself
 * just write — the back button, "clear filters", coming back to the list from a detail screen —
 * replaces what is in the box and cancels the write still waiting, so the box can never go on
 * showing a filter the view has already dropped. Anything else would make the two disagree, and
 * the URL is the one an operator can link, reload and bookmark.
 *
 * @param value - The filter as the view currently holds it.
 * @param commit - Writes the settled text back into the view. Called at most once per pause.
 * @param delayMs - How long to wait after the last keystroke.
 * @returns The text to render, and the change handler to give the input.
 */
export function useDebouncedParam(
  value: string,
  commit: (next: string) => void,
  delayMs: number = SEARCH_DEBOUNCE_MS,
): readonly [string, (next: string) => void] {
  const [draft, setDraft] = useState(value);
  /** The last text this hook put into the view — how it recognises its own write coming back. */
  const written = useRef(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `commit` closes over the current view and is a new function every render, so the pending
  // timer reads it through a ref rather than capturing whichever one was current when it started.
  const latestCommit = useRef(commit);
  useEffect(() => {
    latestCommit.current = commit;
  }, [commit]);

  useEffect(() => {
    if (value === written.current) return;
    // The view moved on its own. Whatever was queued describes a filter nobody asked for now.
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    written.current = value;
    setDraft(value);
  }, [value]);

  // An unmounted page must not still write: the timer would navigate whatever replaced it.
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const change = useCallback(
    (next: string) => {
      setDraft(next);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        written.current = next;
        latestCommit.current(next);
      }, delayMs);
    },
    [delayMs],
  );

  return [draft, change];
}
